import React, { useState } from "react";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Fade,
  Stack,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TruckPickupIcon from "@mui/icons-material/LocalShippingOutlined";
import theme from "../../styles/theme/theme";

const muiTheme = createTheme(theme);

const MODES = [
  {
    value: "self",
    label: "Self Pickup",
    Icon: StorefrontIcon,
    color: "#E8F6EF",
  },
  {
    value: "third",
    label: "Third-Party",
    Icon: TruckPickupIcon,
    color: "#F0F6FF",
  },
  {
    value: "seller",
    label: "Seller to Ship",
    Icon: LocalShippingIcon,
    color: "#FFF6E8",
  },
];

const defaultAddress = () => ({
  fullName: "",
  companyName: "",
  email: "",
  phone: "",
  country: "",
  state: "",
  city: "",
  pincode: "",
  address1: "",
  address2: "",
  gst: "",
  landmark: "",
  locationType: "",
  assistanceAvailable: false,
  equipment: "",
  notes: "",
});

export default function ShippingTabsCard() {
  return (
    <ThemeProvider theme={muiTheme}>
      <ShippingTabs />
    </ThemeProvider>
  );
}

function ShippingTabs() {
  const theme = useTheme();
  const [mode, setMode] = useState("self");
  const [billing, setBilling] = useState(defaultAddress());
  const [pickup, setPickup] = useState(defaultAddress());
  const [delivery, setDelivery] = useState(defaultAddress());

  const showPickup = mode !== "seller";
  const showDelivery = mode === "third" || mode === "seller";

  const handleChange = (setter) => (field) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setter((s) => ({ ...s, [field]: value }));
  };
  const handleToggle = (setter) => (field) => (e) => {
    const checked = !!(e && e.target && e.target.checked);
    setter((s) => ({ ...s, [field]: checked }));
  };

  const submit = () => {
    // replace with API submit
    console.log({
      mode,
      billing,
      pickup: showPickup ? pickup : null,
      delivery: showDelivery ? delivery : null,
    });
    alert("Saved — check console for payload (replace with real submit).");
  };

  const activeColor = MODES.find((m) => m.value === mode)?.color || "#fff";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f7f8fb", p: { xs: 2, md: 6 } }}>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
          Shipping & Address Configuration
        </Typography>

        {/* Card Tabs */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          {MODES.map((m) => (
            <Paper
              key={m.value}
              elevation={m.value === mode ? 6 : 1}
              onClick={() => setMode(m.value)}
              sx={{
                cursor: "pointer",
                p: 2,
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: 2,
                borderRadius: 2,
                border: (theme) =>
                  `1px solid ${
                    m.value === mode ? theme.palette.primary.main : "#e6e9ee"
                  }`,
                bgcolor: m.value === mode ? m.color : "#fff",
                transition: "transform 200ms, box-shadow 200ms",
                transform: m.value === mode ? "translateY(-4px)" : "none",
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "#fff",
                  borderRadius: 1,
                }}
              >
                <m.Icon color={m.value === mode ? "primary" : "action"} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>{m.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {m.value === "self"
                    ? "Buyer collects from seller location"
                    : m.value === "third"
                    ? "Partner picks up & delivers"
                    : "Seller arranges shipping"}
                </Typography>
              </Box>
              {m.value === mode && (
                <Typography variant="caption" color="primary">
                  Active
                </Typography>
              )}
            </Paper>
          ))}
        </Stack>

        {/* Large changing panel */}
        <Fade in key={mode} timeout={300} appear>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, md: 4 },
              borderRadius: 2,
              mb: 3,
              bgcolor: activeColor,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
              {mode === "self"
                ? "Pickup Details"
                : mode === "third"
                ? "Pickup & Delivery Details"
                : "Delivery Details"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fill the relevant address block(s) below. Mandatory notes must be
              provided where required.
            </Typography>

            <Grid container spacing={2}>
              {/* Pickup block (for self & third) */}
              {showPickup && (
                <Grid item xs={12} md={showDelivery ? 6 : 12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid #e8eaef",
                      borderRadius: 2,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontWeight: 700 }}
                    >
                      Pickup Address
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Contact Name *"
                          fullWidth
                          value={pickup.fullName}
                          onChange={handleChange(setPickup)("fullName")}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Phone *"
                          fullWidth
                          value={pickup.phone}
                          onChange={handleChange(setPickup)("phone")}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Country *"
                          fullWidth
                          value={pickup.country}
                          onChange={handleChange(setPickup)("country")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="State *"
                          fullWidth
                          value={pickup.state}
                          onChange={handleChange(setPickup)("state")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="City *"
                          fullWidth
                          value={pickup.city}
                          onChange={handleChange(setPickup)("city")}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Pincode *"
                          fullWidth
                          value={pickup.pincode}
                          onChange={handleChange(setPickup)("pincode")}
                        />
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <TextField
                          label="Address Line 1 *"
                          fullWidth
                          value={pickup.address1}
                          onChange={handleChange(setPickup)("address1")}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Address Line 2 (optional)"
                          fullWidth
                          value={pickup.address2}
                          onChange={handleChange(setPickup)("address2")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="Location Type"
                          value={pickup.locationType}
                          onChange={handleChange(setPickup)("locationType")}
                        >
                          {["Warehouse", "Factory", "Home", "Yard"].map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!pickup.assistanceAvailable}
                              onChange={handleToggle(setPickup)(
                                "assistanceAvailable"
                              )}
                            />
                          }
                          label={
                            pickup.assistanceAvailable
                              ? "Assistance Available"
                              : "No Assistance"
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="Loading Equipment"
                          value={pickup.equipment}
                          onChange={handleChange(setPickup)("equipment")}
                        >
                          {["Forklift", "Crane", "Manual", "None"].map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Pickup Notes *"
                          fullWidth
                          multiline
                          minRows={4}
                          value={pickup.notes}
                          onChange={handleChange(setPickup)("notes")}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}

              {/* Delivery block (for third & seller) */}
              {showDelivery && (
                <Grid item xs={12} md={showPickup ? 6 : 12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: "1px solid #e8eaef",
                      borderRadius: 2,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontWeight: 700 }}
                    >
                      Delivery Address
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Contact Name *"
                          fullWidth
                          value={delivery.fullName}
                          onChange={handleChange(setDelivery)("fullName")}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Phone *"
                          fullWidth
                          value={delivery.phone}
                          onChange={handleChange(setDelivery)("phone")}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Country *"
                          fullWidth
                          value={delivery.country}
                          onChange={handleChange(setDelivery)("country")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="State *"
                          fullWidth
                          value={delivery.state}
                          onChange={handleChange(setDelivery)("state")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="City *"
                          fullWidth
                          value={delivery.city}
                          onChange={handleChange(setDelivery)("city")}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Pincode *"
                          fullWidth
                          value={delivery.pincode}
                          onChange={handleChange(setDelivery)("pincode")}
                        />
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <TextField
                          label="Address Line 1 *"
                          fullWidth
                          value={delivery.address1}
                          onChange={handleChange(setDelivery)("address1")}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Address Line 2 (optional)"
                          fullWidth
                          value={delivery.address2}
                          onChange={handleChange(setDelivery)("address2")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="Location Type"
                          value={delivery.locationType}
                          onChange={handleChange(setDelivery)("locationType")}
                        >
                          {["Warehouse", "Factory", "Home", "Yard"].map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!delivery.assistanceAvailable}
                              onChange={handleToggle(setDelivery)(
                                "assistanceAvailable"
                              )}
                            />
                          }
                          label={
                            delivery.assistanceAvailable
                              ? "Assistance Available"
                              : "No Assistance"
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="Unloading Equipment"
                          value={delivery.equipment}
                          onChange={handleChange(setDelivery)("equipment")}
                        >
                          {["Forklift", "Crane", "Manual", "None"].map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Delivery Notes *"
                          fullWidth
                          multiline
                          minRows={4}
                          value={delivery.notes}
                          onChange={handleChange(setDelivery)("notes")}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Fade>

        {/* Billing block (always visible and at bottom) */}
        <Paper elevation={1} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            Billing Address
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Required for every order
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Full Name *"
                fullWidth
                value={billing.fullName}
                onChange={handleChange(setBilling)("fullName")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Company (optional)"
                fullWidth
                value={billing.companyName}
                onChange={handleChange(setBilling)("companyName")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Email *"
                fullWidth
                value={billing.email}
                onChange={handleChange(setBilling)("email")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone *"
                fullWidth
                value={billing.phone}
                onChange={handleChange(setBilling)("phone")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Country *"
                fullWidth
                value={billing.country}
                onChange={handleChange(setBilling)("country")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="State *"
                fullWidth
                value={billing.state}
                onChange={handleChange(setBilling)("state")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="City *"
                fullWidth
                value={billing.city}
                onChange={handleChange(setBilling)("city")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Pincode *"
                fullWidth
                value={billing.pincode}
                onChange={handleChange(setBilling)("pincode")}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label="Address Line 1 *"
                fullWidth
                value={billing.address1}
                onChange={handleChange(setBilling)("address1")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address Line 2 (optional)"
                fullWidth
                value={billing.address2}
                onChange={handleChange(setBilling)("address2")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="GST / Tax ID (optional)"
                fullWidth
                value={billing.gst}
                onChange={handleChange(setBilling)("gst")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Landmark (optional)"
                fullWidth
                value={billing.landmark}
                onChange={handleChange(setBilling)("landmark")}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 1,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  setBilling(defaultAddress());
                  setPickup(defaultAddress());
                  setDelivery(defaultAddress());
                  setMode("self");
                }}
              >
                Reset
              </Button>
              <Button variant="contained" onClick={submit}>
                Save Shipping
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
}
  